import gql from "graphql-tag";
import RankedIssuerFields from "lib/fragments/RankedIssuerFields";
import ExecutiveCompensationHistoryItemFields from "lib/graphql/fragments/ExecutiveCompensationHistoryItemFields";
import CoverFields from "lib/graphql/fragments/issuer/CoverFields";
import MorningstarPersonFields from "lib/graphql/fragments/MorningstarPersonFields";
import TrailingReturnsFields from "lib/graphql/fragments/TrailingReturnsFields";

export default gql`
  fragment AmericanCompanyIssuerTopLevel on AmericanCompanyIssuer {
    _id
    irs
    name
    sector
    isREIT
    exchange
    listingExchange
    businessDescription
    shortBusinessDescription
    tickerSymbols

    tickerSymbolPrefix
    morningstarFinancialsCurrency

    primaryTickerSymbol
    placeOfIncorporation
    isAmericanDepositaryReceipt
    viewerDidBookmark
    websiteHref
    investorRelationsHref
    edgarHref
    nasdaqHref(tickerSymbol: $tickerSymbol)
    morningstarHref(tickerSymbol: $tickerSymbol)
    foundedYear
    ipoDate
    standardAndPoorsIndicator
    employeeNumbers {
      totalOfEmployees
      asOfDate
    }
    isInPortfolio
    marketCapitalization {
      totalCapitalization
      asOfDate
    }
    address {
      addressLine1
      addressLine2
      addressLine3
      addressLine4
      city
      state
      country
      postalCode
    }
    securities {
      tickerSymbol
      ipoDate
      isPrimaryShare
      depositoryReceiptType
      depositoryReceiptRatio
      investmentType
      trailingReturns {
        ...TrailingReturnsFields
      }
    }
    standardLogo {
      url
      geometry
      dominantColor
      colorPalette
    }
    niceLogo {
      url
      geometry
      dominantColor
      colorPalette
    }
    financialsTemplate {
      reportStyleName
      industryTemplateName
      templateCode
      globalTemplateCode
    }
    modernFinancials(tickerSymbol: $tickerSymbol) {
      ...AmericanCompanyIssuerFinancialsFields
    }
    ...RankedIssuerFields
    youtubeVideos {
      ...YouTubeVideoFields
    }
    shares(tickerSymbol: $tickerSymbol) {
      tsoDate
      companyTso
      floatDate
      floatShare
      floatShareToTotalSharesOutstanding
    }
    covers {
      ...CoverFields
    }
    nextUpcomingCalendarEvent {
      eventDate
      eventType

      ... on EarningsResultsEvent {
        eventFiscalYear
      }
    }
    glassdoorRatings {
      glassdoorUrl
      asOfDate
      allReviewsCount
      ratedReviewsCount
      filteredReviewsCount
      overallRating
      reviewCount
      ceoRating
      ceoRatingsCount
      recommendToFriendRating
      cultureAndValuesRating
      diversityAndInclusionRating
      careerOpportunitiesRating
      workLifeBalanceRating
      seniorManagementRating
      compensationAndBenefitsRating
      businessOutlookRating
    }
    chiefExecutiveOfficer {
      ...MorningstarPersonFields
      executiveCompensationHistoryInUSD: executiveCompensationHistory(currency: "USD") {
        ...ExecutiveCompensationHistoryItemFields
      }
      executiveCompensationHistoryInBRL: executiveCompensationHistory(currency: "BRL") {
        ...ExecutiveCompensationHistoryItemFields
      }
    }
  }

  ${MorningstarPersonFields}
  ${ExecutiveCompensationHistoryItemFields}
  ${RankedIssuerFields}
  ${CoverFields}
  ${TrailingReturnsFields}

  fragment AmericanCompanyIssuerFinancialsFields on AmericanCompanyIssuerFinancials {
    yearly {
      ...AmericanCompanyIssuerFinancialsRowFields
    }
    quarterly {
      ...AmericanCompanyIssuerFinancialsRowFields
    }
  }

  fragment AmericanCompanyIssuerFinancialsRowFields on AmericanCompanyIssuerFinancialsRow {
    reportDate
    linkHref
    fiscalYear
    statementType
    incomeStatement {
      ...AmericanCompanyIssuerFinancialsIncomeStatementFields
    }
    balanceSheet {
      ...AmericanCompanyIssuerFinancialsBalanceSheetFields
    }
    cashFlow {
      ...AmericanCompanyIssuerFinancialsCashFlowFields
    }
    ratios {
      ...AmericanCompanyIssuerFinancialsRatiosFields
    }
  }

  fragment AmericanCompanyIssuerFinancialsRatiosFields on AmericanCompanyIssuerFinancialsRatios {
    buybackRatio
    dividendPayoutRatio
    dividendYield
  }

  fragment AmericanCompanyIssuerFinancialsCashFlowFields on AmericanCompanyIssuerFinancialsCashFlow {
    cashFlowFromOperations
    cashFlowFromInvesting
    cashFlowFromFinancing
    capitalExpenditure
    issuanceOfStock
    repurchaseOfStock
    issuanceOfDebt
    cashFlowForDividends
    freeCashFlow
    changesInCash
    purchaseOfInvestmentProperties
    fundsFromOperations
  }

  fragment AmericanCompanyIssuerFinancialsBalanceSheetFields on AmericanCompanyIssuerFinancialsBalanceSheet {
    cash
    totalAssets
    debt
    totalLiabilities
    retainedEarnings
    treasuryStock
    totalEquity
    netDebt
    netDebtToEbitdaRatio
    debtToTotalEquityRatio
    returnOnEquity
    returnOnInvestedCapital
    debtToTotalAssetsRatio
  }

  fragment AmericanCompanyIssuerFinancialsIncomeStatementFields on AmericanCompanyIssuerFinancialsIncomeStatement {
    interestExpense
    interestIncome
    netInterestIncome
    nonInterestIncome
    creditLossesProvision
    creditLossesProvisionToNetIncomeRatio
    nonInterestExpense
    otherNonInterestExpense
    ffoPerShare
    totalPremiumsEarned
    netInvestmentIncome
    feesAndOtherIncome
    netPolicyholderBenefitsAndClaims
    policyAcquisitionExpense
    otherExpense
    totalExpenses
    revenue
    costOfGoodsSold
    grossProfit
    grossMargin
    sellingGeneralAndAdministration
    researchAndDevelopment
    otherOperatingExpenses
    totalOperatingExpenses
    operatingIncome
    operatingMargin
    ebit
    ebitda
    depreciationAndAmortization
    ebitMargin
    ebitdaMargin
    financialResult
    otherIncomeExpense
    pretaxIncome
    taxProvision
    taxRate
    netIncomeDiscontinuedOperations
    minorityInterest
    netIncome
    netMargin
    epsDiluted
    dividendPerShare
    sharesOutstandingDilutedAverage
  }
`;
